import { useCallback, useEffect, useState } from 'react';
import {
  appendExternalAccessTokenQuery,
  ensureExternalAccessFresh,
  getExternalAccessToken,
} from './api';

const prefetchedUrls = new Set<string>();

/** Предзагрузка фото детекции (токен + Image) для быстрого открытия модалки. */
export async function prefetchAuthenticatedImage(resourceUrl: string | null | undefined): Promise<void> {
  if (!resourceUrl || prefetchedUrls.has(resourceUrl)) return;

  prefetchedUrls.add(resourceUrl);

  try {
    await ensureExternalAccessFresh();
    const token = getExternalAccessToken();
    if (!token) return;

    const url = appendExternalAccessTokenQuery(resourceUrl, token);
    await new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = url;
    });
  } catch {
    prefetchedUrls.delete(resourceUrl);
  }
}

/**
 * Подготавливает URL фото с ?token= (external JWT) для <img src={...}>.
 * Поле blobUrl — историческое имя, это обычный https-URL, не blob:.
 */
export function useAuthenticatedBlobUrl(resourceUrl: string | null, enabled = true) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled || !resourceUrl) {
      setBlobUrl(null);
      setError(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const applyUrl = (token: string) => {
      if (cancelled) return;
      setBlobUrl(appendExternalAccessTokenQuery(resourceUrl, token));
      setError(false);
      setLoading(false);
    };

    const existingToken = getExternalAccessToken();
    if (existingToken) {
      applyUrl(existingToken);
    } else {
      setLoading(true);
      setError(false);
      setBlobUrl(null);
    }

    ensureExternalAccessFresh()
      .then(() => {
        if (cancelled) return;
        const token = getExternalAccessToken();
        if (!token) {
          setError(true);
          setLoading(false);
          return;
        }
        applyUrl(token);
      })
      .catch(() => {
        if (!cancelled && !getExternalAccessToken()) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resourceUrl, enabled]);

  const onImageError = useCallback(() => setError(true), []);

  return { blobUrl, loading, error, onImageError };
}
